import { action, isObservableArray, makeObservable, observable } from 'mobx'
import moment from 'moment'
import * as R from 'ramda'
import { cloneDeep } from 'lodash'
import i18n from 'i18next'
import { v4 as uuidv4 } from 'uuid'

import { getProductMap } from '../../../../utils/helpers'

class customTagStore {
    @observable isLoading = true

    formFields = {}

    clients = []

    tagList = []

    @observable searchData = []

    productMap = {}

    @observable industries = []

    @observable productTypes = []

    @observable respondentTypes = []

    @observable surveyTypes = []

    divisions = []

    @observable portfolios = []

    @observable showBenchmarksModal = false

    @observable benchamrkOptionIndex = undefined

    @observable isCustomBenchmark = false

    @observable customTag = {
        name: undefined,
        description: undefined,
        clientId: undefined,
        oid: undefined,
        industry: undefined,
        productType: undefined,
        respondentType: undefined,
        surveyType: undefined,
        divisionId: [],
        portfolioId: [],
        questionType: undefined,
        uiQuestionType: undefined,
        options: [],
        defaultValue: false,
        config: {
            isMandatory: false,
            answerOptions: [],
            mergefields: undefined,
            dateOptions: [],
            benchmarkOptions: [],
            benchmarkRules: [],
        },
    }

    @observable testBenchmark = undefined

    constructor(store, isCustomBenchmark = false) {
        makeObservable(this)
        this.store = store
        this.isCustomBenchmark = isCustomBenchmark
    }

    register = (name, validate) => {
        const { formFields } = this
        formFields[name] = validate
    }

    unregister = (name) => {
        const { formFields } = this
        Reflect.deleteProperty(formFields, name)
    }

    @action
    onBlur = async ({ name, value, type }, promise, fieldReset, path) => {
        let fieldValue = value

        if (type === 'checkboxgroup' || type === 'benchmarkOptions') {
            fieldValue = value.slice()
        } else if (Array.isArray(value) || isObservableArray(value)) {
            fieldValue = value.slice().map(({ value }) => value)
        }

        if (fieldReset) fieldReset(this.customTag, fieldValue)

        if (promise) await promise(fieldValue, this.customTag)

        const lens = R.lensPath(path)
        this.customTag = R.set(lens, fieldValue, this.customTag)
    }

    getLevels = () => (
        [
            {
                value: 3,
                label: 'Client',
            },
            {
                value: 4,
                label: 'Division',
            },
            {
                value: 5,
                label: 'Portfolio',
            },
            {
                value: 6,
                label: 'Event',
            },
            {
                value: 7,
                label: 'Survey',
            },
            {
                value: 9,
                label: 'Benchmark',
                isHidden: !this.isCustomBenchmark,
            },
            {
                value: 10,
                label: 'Integration',
            },
            {
                value: 11,
                label: 'User',
            },
        ].filter(({ isHidden }) => (
            !isHidden
        ))
    )

    getOptions = () => (
        [
            {
                value: 'mergefield',
                label: i18n.t('customTag.edit.option.mergefield'),
                isHidden: !(
                    ['SB', 'CH', 'TX', 'I'].includes(this.customTag.questionType)
                    && [6, 7, 10].includes(this.customTag.oid)
                ),
            },
            {
                value: 'benchmark',
                label: i18n.t('customTag.edit.option.benchmark'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && ![7, 10].includes(this.customTag.oid)
                    && this.isCustomBenchmark
                ),
            },
            {
                value: 'qlist',
                label: i18n.t('customTag.edit.option.qlist'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && [4, 5, 6].includes(this.customTag.oid)
                ),
            },
            {
                value: 'registration',
                label: i18n.t('customTag.edit.option.registration'),
                isHidden: !(
                    this.customTag.questionType === 'TX'
                    && this.customTag.oid === 7
                ),
            },
            {
                value: 'template',
                label: i18n.t('customTag.edit.option.template'),
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                    && [3, 4, 5, 6, 7].includes(this.customTag.oid)
                ),
            },
        ].filter(({ isHidden }) => (
            !isHidden
        ))
    )

    getQuestionTypes = () => (
        [
            {
                value: 'SB',
                label: 'Drop Down',
            },
            {
                value: 'CH',
                label: 'Multi-Choice',
            },
            {
                value: 'CH-AT', // Multi-choice locked with a single admin tag (one option only)
                label: 'Admin Tag',
            },
            {
                value: 'TX',
                label: 'Text',
            },
            {
                value: 'I',
                label: 'Numeric',
            },
            {
                value: 'DT',
                label: 'Date',
                isHidden: this.customTag.oid !== 7,
            },
        ].filter(({ isHidden }) => !isHidden)
    )

    getFormFields = () => {
        const { customTag } = this
        const fields = [
            {
                name: 'name',
                label: i18n.t('customTag.edit.name'),
                type: 'input',
                path: ['name'],
                isMandatory: true,
            },
            {
                name: 'description',
                label: i18n.t('customTag.edit.description'),
                type: 'input',
                path: ['description'],
            },
            {
                name: 'clientId',
                label: i18n.t('client'),
                type: 'select',
                path: ['clientId'],
                options: this.clients,
                isMandatory: true,
                promise: (value) => this.getClient(value),
                fieldResets: (model) => {
                    model.divisionId = []
                    model.portfolioId = []
                    model.industry = undefined
                    model.productType = undefined
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
            },
            {
                name: 'oid',
                label: i18n.t('customTag.edit.level'),
                type: 'select',
                path: ['oid'],
                options: this.getLevels(),
                isMandatory: true,
                fieldResets: (model) => {
                    model.options = []
                    model.mergefield = undefined
                },
                isHidden: this.isCustomBenchmark,
            },
            {
                name: 'industry',
                label: i18n.t('industry'),
                type: 'select',
                path: ['industry'],
                options: this.industries,
                isMandatory: true,
                isHidden: this.industries.length === 0,
                promise: (value) => this.setProductTypes(value),
                fieldResets: (model) => {
                    model.productType = undefined
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
            },
            {
                name: 'productType',
                label: i18n.t('product_type'),
                type: 'select',
                path: ['productType'],
                options: this.productTypes,
                isMandatory: this.customTag.oid !== 9,
                isHidden: R.isNil(customTag.industry) || this.customTag.oid < 6,
                promise: (value) => this.setSurveyTypes(value),
                fieldResets: (model) => {
                    model.respondentType = undefined
                    model.surveyType = undefined
                },
            },
            {
                name: 'surveyType',
                label: i18n.t('survey_type'),
                type: 'select',
                path: ['surveyType'],
                options: this.surveyTypes,
                isMandatory: this.customTag.oid !== 9,
                isHidden: R.isNil(customTag.productType) || this.customTag.oid < 7,
                promise: (value) => this.setRespondentTypes(value),
                fieldResets: (model) => {
                    model.respondentType = undefined
                },
            },
            {
                name: 'respondentType',
                label: i18n.t('respondent_type'),
                type: 'select',
                path: ['respondentType'],
                options: this.respondentTypes,
                isMandatory: this.customTag.oid !== 9,
                isHidden: R.isNil(customTag.surveyType) || this.customTag.oid < 7,
            },
            {
                name: 'divisionId',
                label: i18n.t('division'),
                type: 'select',
                path: ['divisionId'],
                options: this.divisions,
                isMulti: true,
                isHidden: R.pathOr(0, ['customTag', 'clientId'], this) === 0,
                promise: (value) => this.setPortfolios(value),
                fieldResets: (model, value) => {
                    const portfolioIdSet = new Set(model.portfolioId)
                    model.portfolioId = this.clientPortfolios
                        .filter(({ value: portfolioId, divisionId }) => (
                            portfolioIdSet.has(portfolioId) && value.includes(divisionId)
                        ))
                        .map(({ value }) => value)
                },
            },
            {
                name: 'portfolioId',
                label: i18n.t('portfolio'),
                type: 'select',
                path: ['portfolioId'],
                options: this.portfolios,
                isMulti: true,
                isHidden: R.pathOr(0, ['customTag', 'clientId'], this) === 0 || R.pathOr(9, ['customTag', 'oid'], this) > 7,
            },
            {
                name: 'questionType',
                label: i18n.t('customTag.edit.questionType'),
                type: 'select',
                path: ['uiQuestionType'],
                options: this.getQuestionTypes(),
                isMandatory: true,
                isHidden: (
                    R.pathOr(9, ['customTag', 'oid'], this) === 9
                ),
                fieldResets: (model, value) => {
                    model.questionType = value === 'CH-AT' ? 'CH' : value
                    model.options = value === 'CH-AT'
                        ? R.union(model.options, ['adminTag'])
                        : model.options.filter((option) => option !== 'adminTag')
                    model.config.answerOptions = value === 'CH-AT' // auto-add 1 options for CH-AT
                        ? [{ uuid: uuidv4(), config: { langs: { en_GB: '' } } }]
                        : []
                    model.config.mergefield = undefined
                    model.config.dateOptions = []
                },
            },
            {
                name: 'options',
                label: i18n.t('customTag.edit.options'),
                type: 'checkboxGroup',
                path: ['options'],
                options: this.getOptions(),
                isHidden: (
                    R.isNil(this.customTag.oid)
                        || R.isNil(this.customTag.questionType)
                        || this.customTag.options.includes('adminTag')
                ),
                fieldResets: (model, options) => {
                    if (!options.includes('mergefield')) {
                        model.config.mergefield = undefined
                    }
                    if (!options.includes('benchamrk')) {
                        model.benchmarks = undefined
                    }
                },
            },
            {
                name: 'isMandatory',
                label: 'Mandatory',
                type: 'checkbox',
                path: ['config', 'isMandatory'],
                isHidden: (
                    R.isNil(this.customTag.oid)
                        || R.isNil(this.customTag.questionType)
                        || this.customTag.questionType === 'DT'
                        || this.customTag.options.includes('adminTag')
                ),
            },
            {
                name: 'defaultValue',
                label: 'Default value',
                type: 'switch',
                path: ['defaultValue'],
                isHidden: !this.customTag.options.includes('adminTag'),
            },
            {
                name: 'mergefield',
                type: 'mergefield',
                path: ['customTag', 'mergefield'],
                isHidden: !this.customTag.options.includes('mergefield'),
            },
            {
                name: 'answerOptions',
                type: 'answerOptions',
                path: ['customTag', 'config', 'answerOptions'],
                isHidden: !(
                    ['SB', 'CH'].includes(this.customTag.questionType)
                ),
            },
            {
                name: 'date',
                type: 'date',
                path: ['customTag', 'date'],
                isHidden: this.customTag.questionType !== 'DT',
            },
            {
                name: 'benchmarks',
                type: 'benchmarks',
                path: ['customTag', 'benchmarks'],
                isHidden: R.isNil(customTag.respondentType) || !(this.customTag.options.includes('benchmark') || this.customTag.oid === 9),
            },
        ]

        return fields.filter(({ isHidden }) => !isHidden)
    }

    @action
    resetState = () => {
        this.productMap = {}
        this.industries = []
        this.productTypes = []
        this.respondentTypes = []
        this.surveyTypes = []
        this.divisions = []
        this.portfolios = []
        this.customTag = {
            name: undefined,
            description: undefined,
            clientId: undefined,
            oid: this.isCustomBenchmark ? 9 : undefined,
            industry: undefined,
            productType: undefined,
            respondentType: undefined,
            surveyType: undefined,
            divisionId: [],
            portfolioId: [],
            questionType: undefined,
            uiQuestionType: undefined,
            options: [],
            defaultValue: false,
            config: {
                mergefield: undefined,
                dateOptions: [],
                answerOptions: [],
                isMandatory: false,
                benchmarkOptions: [],
                benchmarkRules: [],
            },
        }
    }

    @action
    initEdit = async (id) => {
        const { store } = this

        this.resetState()
        if (id === 0) {
            if (this.clients.length === 1) {
                this.customTag.clientId = R.path([0, 'value'], this.clients)
            }
            return
        }

        this.isLoading = true

        const response = await store.http.get(`/intranet/customTag/${id}`)

        this.customTag = R.mergeRight(this.customTag, this.fromJSON(response))

        console.log('initEdit', JSON.stringify(this.customTag, null, 4))

        await this.getClient(this.customTag.clientId)
        this.setPortfolios(this.customTag.divisionId)
        this.setProductTypes(this.customTag.industry)
        this.setSurveyTypes(this.customTag.productType)
        this.setRespondentTypes(this.customTag.surveyType)

        this.isLoading = false
    }

    initSearch = async () => {
        const { store } = this
        try {
            const response = await store.http.get('/intranet/customTag')
            this.tagList = response
            this.searchData = this.isCustomBenchmark
                ? this.tagList.filter((t) => t.oid === 9)
                : this.tagList
        } catch (e) {
            console.error(e)
            store.addNotitication(null, 'Error getting tags', 'error')
        }
    }

    filter = () => {
        const { searchStr } = this.searchModule
        const re = new RegExp(searchStr, 'i')
        const filteredData = R.isEmpty(searchStr)
            ? this.tagList
            : this.tagList.filter(({
                id,
                name,
                oid,
                clientId,
                industry,
                productType,
                surveyType,
                respondentType,
            }) => (
                (String(id).match(re)
                || name.match(re)
                || String(oid).match(re)
                || String(clientId).match(re)
                || industry?.match(re)
                || productType?.match(re)
                || surveyType?.match(re)
                || respondentType?.match(re))
                && ((this.isCustomBenchmark && oid === 9) || !this.isCustomBenchmark)
            ))

        this.searchData = filteredData
    }

    @action
    getClient = async (clientId) => {
        const { store } = this
        const { http } = store

        const [productMap] = await Promise.all([
            getProductMap(http, clientId, clientId === 0),
            this.getDivisions(clientId),
            this.getPortfolios(clientId),
        ])

        this.productMap = productMap
        this.industries = Object.values(this.productMap).map(({ label, value }) => ({
            label,
            value,
        }))
    }

    setPortfolios = (divisionId) => {
        this.portfolios = this.clientPortfolios.filter((portfolio) => (
            divisionId.length === 0 || divisionId.includes(portfolio.divisionId)
        ))
    }

    setProductTypes = (industry) => {
        const productTypes = R.pathOr({}, [industry, 'children'], this.productMap)
        const mappedProductTypes = Object.values(productTypes).map(({ label, value }) => ({
            label,
            value,
        }))

        this.productTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedProductTypes)
            : mappedProductTypes
    }

    setSurveyTypes = (productType) => {
        const surveyTypes = R.pathOr({}, [this.customTag.industry, 'children', productType, 'children'], this.productMap)
        const mappedSurveyTypes = Object.values(surveyTypes).map(({ label, value }) => ({
            label,
            value,
        }))

        this.surveyTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedSurveyTypes)
            : mappedSurveyTypes
    }

    setRespondentTypes = (surveyType) => {
        const respondentTypes = R.pathOr({}, [this.customTag.industry, 'children', this.customTag.productType, 'children', surveyType, 'children'], this.productMap)
        const mappedRespondentTypes = Object.values(respondentTypes).map(({ label, value }) => ({
            label,
            value,
        }))

        this.respondentTypes = this.isCustomBenchmark
            ? [{ value: '', label: i18n.t('all') }].concat(mappedRespondentTypes)
            : mappedRespondentTypes
    }

    getDivisions = async (clientId) => {
        let divisions = []

        try {
            if (clientId !== 0) {
                divisions = await this.store.http.get(`/division/client/${clientId}`)
            }
        } catch (e) {
            console.error(e)
        }

        this.divisions = divisions.map(({ divisionId, name }) => ({
            value: divisionId,
            label: name,
        }))
    }

    getPortfolios = async (clientId) => {
        let portolios = []

        try {
            if (clientId !== 0) {
                portolios = await this.store.http.get(`/portfolio/client/${clientId}`)
            }
        } catch (e) {
            console.error(e)
        }

        this.clientPortfolios = portolios.map(({ divisionId, portfolioId, name }) => ({
            value: portfolioId,
            label: name,
            divisionId,
        }))

        this.portfolios = this.clientPortfolios
    }

    getClients = async () => {
        const { userData } = this.store
        const { clientId, clientName } = userData
        let clients = []
        try {
            const response = await this.store.http.get('/client')
            if (response.clients.length === 0) {
                clients = [{
                    clientId,
                    name: clientName,
                }]
            } else {
                clients = [{
                    clientId: 0,
                    name: i18n.t('customTag.edit.allClients'),
                }].concat(response.clients)
            }
        } catch (e) {
            clients = [{
                clientId,
                name: clientName,
            }]
        }

        this.clients = clients.map(({ clientId, name }) => ({
            value: clientId,
            label: `${name} [${clientId}]`,
        }))
    }

    @action
    init = async () => {
        await Promise.all([
            this.getClients(),
        ])
        if (this.isCustomBenchmark) {
            this.customTag.oid = 9
        }

        this.isLoading = false
    }

    cancel = (history) => {
        const url = this.isCustomBenchmark
            ? '/intranet/customBenchmark'
            : '/intranet/customTag'

        history.push(url)
    }

    isValid = () => (
        Object.entries(this.formFields)
            .map(([name, validate]) => (
                validate({
                    name,
                    value: this.customTag[name],
                }, true)
            ))
            .every((isValid) => isValid)
    )

    fromJSON = (json) => {
        const customTag = cloneDeep(json)

        customTag.divisionId = R.pathOr([], ['divisionId'], customTag).split(',').filter((v) => !R.isEmpty(v)).map((v) => parseInt(v, 10))
        customTag.portfolioId = R.pathOr([], ['portfolioId'], customTag).split(',').filter((v) => !R.isEmpty(v)).map((v) => parseInt(v, 10))
        customTag.options = R.pathOr([], ['options'], customTag).split(',')

        const dateOptions = R.pathOr([], ['config', 'dateOptions'], customTag)
            .map((option) => {
                option.config.startDate = R.isNil(option.config.startDate)
                    ? undefined
                    : moment(option.config.startDate, 'DD/MM')

                option.config.endDate = R.isNil(option.config.endDate)
                    ? undefined
                    : moment(option.config.endDate, 'DD/MM')

                return option
            })

        customTag.config = {
            mergefield: R.pathOr(undefined, ['config', 'mergefield'], customTag),
            answerOptions: R.pathOr([], ['config', 'answerOptions'], customTag),
            benchmarkOptions: R.pathOr([], ['config', 'benchmarkOptions'], customTag),
            benchmarkRules: R.pathOr([], ['config', 'benchmarkRules'], customTag),
            dateOptions,
            isMandatory: customTag.config.isMandatory,
        }

        customTag.uiQuestionType = customTag.options.includes('adminTag') ? 'CH-AT' : customTag.questionType
        customTag.defaultValue = Boolean(R.pathOr(false, ['defaultValue'], customTag))

        return customTag
    }

    toJSON = () => {
        const { customTag } = this

        return {
            id: customTag.id,
            name: customTag.name,
            description: customTag.description,
            clientId: customTag.clientId,
            oid: customTag.oid,
            industry: customTag.industry,
            productType: customTag.productType,
            respondentType: customTag.respondentType,
            surveyType: customTag.surveyType,
            divisionId: customTag.divisionId.slice().join(','),
            portfolioId: customTag.portfolioId.slice().join(','),
            questionType: customTag.questionType,
            options: customTag.options.slice().join(','),
            defaultValue: customTag.defaultValue,
            config: {
                isMandatory: customTag.config.isMandatory,
                mergefield: customTag.config.mergefield,
                answerOptions: customTag.config.answerOptions.slice(),
                dateOptions: R.pathOr([], ['config', 'dateOptions'], customTag)
                    .slice()
                    .map(({
                        uuid, config,
                    }) => ({
                        uuid,
                        config: {
                            langs: config.langs,
                            startDate: !R.isNil(config.startDate) ? config.startDate.format('DD/MM') : undefined,
                            endDate: !R.isNil(config.endDate) ? config.endDate.format('DD/MM') : undefined,
                            repeat: config.repeat,
                        },
                    })),
                benchmarkOptions: R.pathOr([], ['config', 'benchmarkOptions'], customTag).slice(),
                benchmarkRules: R.pathOr([], ['config', 'benchmarkRules'], customTag).slice().map((bo) => ({
                    ...bo,
                    config: {
                        ...bo.config,
                        rules: R.pathOr([], ['config', 'rules'], bo).slice()
                            .map((rule) => ({ ...rule, values: R.pathOr([], ['values'], rule).slice() })),
                    },
                })),
            },
        }
    }

    save = async (history) => {
        if (this.isValid()) {
            const customTag = this.toJSON()

            const url = R.isNil(customTag.id)
                ? '/intranet/customTag'
                : `/intranet/customTag/${customTag.id}`

            const method = R.isNil(customTag.id)
                ? 'post'
                : 'put'

            try {
                const msg = R.isNil(customTag.id)
                    ? 'msg_Item_Successfully_Created'
                    : 'msg_Item_Successfully_Updated'
                await this.store.http[method](url, JSON.stringify(customTag))
                this.store.addNotification(null, i18n.t(msg), 'success')
                history.push('/intranet/customTag')
            } catch (e) {
                console.error(e)
                const msg = R.isNil(customTag.id)
                    ? 'msg_Item_Failed_Create'
                    : 'msg_Item_Failed_Update'
                this.store.addNotification(null, i18n.t(msg), 'error')
            }
        }
    }

    delete = async (id) => {
        const url = `/intranet/customTag/${id}`
        try {
            await this.store.http.delete(url)
            this.store.addNotification(null, i18n.t('msg_Item_Successfully_Deleted'), 'success')
            this.initSearch()
            window.scrollTo(0, 0)
        } catch (e) {
            console.error(e)
            this.store.addNotification(null, i18n.t('msg_Item_has_not_been_deleted'), 'error')
        }
    }

    toggleShowBenchmarksModal = () => {
        this.showBenchmarksModal = !this.showBenchmarksModal
    }

    setBenchamrkOptionIndex = (index) => {
        this.benchamrkOptionIndex = index
    }

    toggleTestBenchmark = (tagOptionId) => {
        this.testBenchmark = tagOptionId
    }
}

export default customTagStore
